import MapPinHouseIcon from '@/components/icon/MapPinHouseIcon';
import { ButtonCus } from '@/components/common/Button';
import Combobox from '@/components/common/Combobox';
import { Form, FormControl, FormField, FormItem } from '@/components/common/Form';
import Paginate from '@/components/pagination/Paginate';
import { useStore } from '@/hooks/useStore';
import yup from '@/services/yup';
import { FavoriteLocation } from '@/types/location';
import { yupResolver } from '@hookform/resolvers/yup';
import { CirclePlus, MapPin, Pencil } from 'lucide-react';
import { observer } from 'mobx-react-lite';
import { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { LANGUAGES_SUPPORTED } from '@/types/enums';
import { flowResult } from 'mobx';
import LocationCard from './component/LocationCard';

// #region RegistrationLocationTab
export default observer(function RegistrationLocationTab() {
    // hooks
    const { t, i18n: { language } } = useTranslation();

    // store
    const {
        accountStore: { favoriteLocationsList, keepSearchParams, unregisterFavoriteLocation, getListUserFavoriteLocations, clean, setObservable },
        modalStore: { showModal, showAlertModal },
        regionProvinceStore: {
            provinces, getProvinces,
            regions, getRegions, getProvincesByRegion
        }
    } = useStore();

    // state
    const [isEdit, setIsEdit] = useState(false);
    const [newFavoriteLocationsList, setNewFavoriteLocationsList] = useState<FavoriteLocation[]>([]);
    const [idArraySelected, setIdArraySelected] = useState<number[]>([]);

    const validateSchema = yup.object().shape({
        regionId: yup.string(),
        provinceId: yup.string()
    });

    type FormData = yup.InferType<typeof validateSchema>;

    const form = useForm<FormData>({
        resolver: yupResolver(validateSchema),
        mode: 'onSubmit',
        defaultValues: {
            provinceId: keepSearchParams?.provinceId ?? '',
            regionId: keepSearchParams?.regionId ?? ''
        }
    });

    const regionWatch = form.watch('regionId');
    const provinceWatch = form.watch('provinceId');

    // lifecycle
    useEffect(() => {
        getRegions();
        getProvinces();
        getListUserFavoriteLocations({ isFavorite: true });

        return () => {
            clean();
        };
    }, []);

    useEffect(() => {
        onSearch();
    }, [regionWatch, provinceWatch, favoriteLocationsList]);

    // functions
    const handleFilterProvince = (regionId: number | undefined) => {
        if (regionId) {
            getProvincesByRegion(regionId);
        } else {
            getProvinces();
        }
    };

    const handleClickAddLocation = () => {
        showModal({
            id: 'popup-location-list',
            size: '3xl',
            headerClassName: 'hidden',
            footerClassName: 'hidden',
            content: <PopupLocationList />
        });
    };

    const handleRemoveLocation = (locationId: number) => {
        setNewFavoriteLocationsList(newFavoriteLocationsList.filter(item => item.id !== locationId));
        setIdArraySelected([...idArraySelected, locationId]);
    };

    const handleConfirm = async () => {
        const res = await flowResult(unregisterFavoriteLocation({
            locationIds: idArraySelected
        }));
        if (res) {
            showAlertModal({
                id: 'success',
                type: 'success',
                content: t('messages.update_success'),
                onClickXIcon: () => {
                    getListUserFavoriteLocations({ isFavorite: true });
                    onSearch();
                }
            });
        }
        setIsEdit(false);
    };

    const onSearch = () => {
        setObservable('keepSearchParams', {
            regionId: regionWatch,
            provinceId: provinceWatch
        });
        let filteredList = [...favoriteLocationsList];

        if (regionWatch !== '') {
            filteredList = filteredList.filter(item => Number(item?.regionId) === Number(regionWatch));
        }
        if (provinceWatch !== '') {
            filteredList = filteredList.filter(item => Number(item?.provinceId) === Number(provinceWatch));
        }
        setNewFavoriteLocationsList(filteredList);
    };

    const renderButton = () => {
        return (
            <>
                <ButtonCus
                    type='button'
                    variant='default'
                    size='md'
                    onClick={() => {
                        form.reset();
                        setIsEdit(false);
                        handleClickAddLocation();
                    }}
                    className='w-full md:w-52'
                >
                    <CirclePlus className='mr-1' />
                    {t('button.add_location')}
                </ButtonCus>
                <ButtonCus
                    type='button'
                    variant='outline'
                    size='md'
                    disabled={newFavoriteLocationsList.length === 0}
                    onClick={() => setIsEdit(true)}
                    className='w-full md:w-52'
                >
                    <Pencil className='mr-1' />
                    {t('button.edit')}
                </ButtonCus>
            </>
        );
    };

    return (
        <div className='layout-card'>
            <div className='flex flex-col md:flex-row md:items-center md:justify-between gap-2'>
                <h2 className='title-2'>{t('words_title.favorite_location')}</h2>
                <div className='flex justify-center xsm:gap-4 gap-2 lg:hidden'>
                    {!isEdit && renderButton()}
                </div>
            </div>
            <div className='flex-none lg:flex lg:justify-between lg:items-center lg:gap-3 xsm:mt-6 mt-4'>
                <Form {...form}>
                    <form onSubmit={form.handleSubmit(onSearch)}>
                        <div className='grid grid-cols-1 point-375:grid-cols-2 items-center xsm:gap-4 gap-2 flex-1'>
                            <FormField
                                control={form.control}
                                name='regionId'
                                render={({ field }) => (
                                    <FormItem className='w-full'>
                                        <FormControl>
                                            <div className='relative'>
                                                <MapPin className='absolute left-3 top-1/2 -translate-y-1/2 z-10 size-6 text-[#595959]' />
                                                <Combobox
                                                    className='w-full lg:w-[216px] pl-10'
                                                    placeholder={t('field.region')}
                                                    value={field.value ?? ''}
                                                    onChange={(item) => {
                                                        field.onChange(item?.code || '');
                                                        form.setValue('regionId', item?.code || '');
                                                        form.setValue('provinceId', '');
                                                        handleFilterProvince(item?.code);
                                                    }}
                                                    search={false}
                                                    items={
                                                        (Array.isArray(regions) && regions?.length > 0 ?
                                                            [
                                                                ...regions?.filter(item => item.nameJP || item.name).map(item => ({
                                                                    label: language === LANGUAGES_SUPPORTED.Japanese ? item.nameJP : item.name,
                                                                    value: item.id.toString(),
                                                                    code: item.id
                                                                }))
                                                            ] :
                                                            []
                                                        )
                                                    }
                                                />
                                            </div>
                                        </FormControl>
                                    </FormItem>
                                )}
                            />
                            <FormField
                                control={form.control}
                                name='provinceId'
                                render={({ field }) => (
                                    <FormItem className='w-full'>
                                        <FormControl>
                                            <div className='relative'>
                                                <MapPinHouseIcon className='absolute left-3 top-1/2 -translate-y-1/2 z-10 size-6 text-[#595959]' />
                                                <Combobox
                                                    className='w-full lg:w-[216px] pl-10'
                                                    placeholder={t('field.prefectures')}
                                                    value={field.value ?? ''}
                                                    onChange={(item) => {
                                                        field.onChange(item?.code ?? '');
                                                        form.setValue('provinceId', item?.code || '');
                                                    }}
                                                    search={false}
                                                    items={
                                                        (Array.isArray(provinces) && provinces?.length > 0 ?
                                                            [
                                                                ...provinces
                                                                    ?.filter(item => item.nameJP || item.name)
                                                                    .map(item => ({
                                                                        label: language === LANGUAGES_SUPPORTED.Japanese ? item.nameJP : item.name,
                                                                        value: item.id.toString(),
                                                                        code: item.id
                                                                    }))
                                                            ] :
                                                            []
                                                        )
                                                    }
                                                />
                                            </div>
                                        </FormControl>
                                    </FormItem>
                                )}
                            />
                        </div>
                    </form>
                </Form>
                <div className='lg:flex gap-4 hidden'>
                    {!isEdit && renderButton()}
                </div>
            </div>
            <div className='xsm:mt-6 mt-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4 gap-[min(20px,5vw)]'>
                {newFavoriteLocationsList.length > 0 ?
                    (newFavoriteLocationsList.map(location => (
                        <LocationCard
                            key={location.id}
                            location={location}
                            isFavorite={true}
                            isEdit={isEdit}
                            onRemove={() => handleRemoveLocation(location.id)}
                        />
                    ))) :
                    (
                        <div className='flex items-center justify-center w-full col-span-1 sm:col-span-2 lg:col-span-3 2xl:col-span-4 3xl:col-span-5'>{t('sentences.no_data')}</div>
                    )}
            </div>
            {
                isEdit && (
                    <div className='mt-4 flex justify-center items-center space-x-3 md:static md:w-full w-screen md:shadow-none shadow-shadow-3 fixed bottom-0 left-0 py-3 md:bg-none bg-background-3 '>
                        <ButtonCus
                            variant='outline'
                            size='sm'
                            className='w-32'
                            onClick={() => {
                                getListUserFavoriteLocations({ isFavorite: true });
                                setIsEdit(false);
                                setIdArraySelected([]);
                            }}
                        >
                            {t('button.cancel')}
                        </ButtonCus>
                        <ButtonCus
                            variant='default'
                            size='sm'
                            className='w-32'
                            onClick={handleConfirm}
                        >
                            {t('button.save')}
                        </ButtonCus>
                    </div>
                )
            }
        </div>
    );
});
// #endregion

// #region PopupLocationList
const PopupLocationList = observer(function PopupLocationList() {
    // hooks
    const { t, i18n: { language } } = useTranslation();

    // store
    const {
        accountStore: { nonFavoriteLocationsList, paging, totalPage, defaultPaging, setObservable, getListFavoriteLocationsByStatus },
        regionProvinceStore: {
            provinces, getProvinces,
            regions, getRegions, getProvincesByRegion
        }
    } = useStore();

    // validate Schema
    const validateSchema = yup.object().shape({
        regionId: yup.string(),
        provinceId: yup.string()
    });

    type FormData = yup.InferType<typeof validateSchema>;

    const form = useForm<FormData>({
        resolver: yupResolver(validateSchema),
        mode: 'onSubmit',
        defaultValues: {
            regionId: '',
            provinceId: ''
        }
    });

    const watchRegionId = form.watch('regionId');

    // state
    const defaultSearchParams = { size: 9, isFavorite: false };
    const [searchParams, setSearchParams] = useState<any>({ ...defaultSearchParams });

    const locationListRef = useRef<HTMLDivElement>(null);

    // lifecycle
    useEffect(() => {
        getListFavoriteLocationsByStatus({ ...defaultSearchParams });
        getProvinces();
        getRegions();

        return () => {
            form.reset();
            setObservable('paging', defaultPaging, { isMergeObject: false });
        };
    }, []);

    // functions
    const handleFilterProvince = (regionId: number | undefined) => {
        if (regionId) {
            getProvincesByRegion(regionId);
        } else {
            getProvinces();
        }
    };

    const onApply = async (data: FormData) => {
        const params = {
            ...defaultSearchParams,
            regionId: data?.regionId ? Number(data?.regionId) : undefined,
            provinceId: data?.provinceId ? Number(data?.provinceId) : undefined
        };
        setSearchParams(params);
        getListFavoriteLocationsByStatus(params);
    };

    const onSearch = (data: any) => {
        const { regionId, provinceId } = data;

        const params = {
            ...defaultSearchParams,
            regionId: regionId ? Number(regionId) : undefined,
            provinceId: provinceId ? Number(provinceId) : undefined
        };
        setSearchParams(params);
        setObservable('paging', defaultPaging, { isMergeObject: false });

        getListFavoriteLocationsByStatus(params, defaultPaging);
    };

    const onScrollToTopList = () => {
        if (locationListRef.current) {
            const modalContainer = locationListRef.current.closest('#locations-register-modal') as HTMLElement;
            if (modalContainer) {
                modalContainer.scrollTo({ top: 0, behavior: 'smooth' });
            }
        }
    };

    return (
        <div className='flex flex-col max-h-[70vh] relative'>
            <Form {...form}>
                <form onSubmit={form.handleSubmit(onApply)}>
                    <div className='grid grid-cols-1 point-375:grid-cols-2 items-end md:gap-6 gap-2'>
                        <FormField
                            control={form.control}
                            name='regionId'
                            render={({ field }) => (
                                <FormItem className='w-full'>
                                    <FormControl className='mt-[10px]'>
                                        <div className='relative'>
                                            <MapPin className='absolute left-3 top-1/2 -translate-y-1/2 z-10 size-6 text-[#595959]' />
                                            <Combobox
                                                className='w-full pl-10'
                                                placeholder={t('field.region')}
                                                value={field.value ?? ''}
                                                onChange={(item) => {
                                                    field.onChange(item?.value);
                                                    form.setValue('provinceId', '');
                                                    handleFilterProvince(item?.code);

                                                    onSearch({
                                                        provinceId: undefined,
                                                        regionId: item?.value
                                                    });
                                                }}
                                                search={false}
                                                items={
                                                    (Array.isArray(regions) && regions?.length > 0 ?
                                                        [
                                                            ...regions?.filter(item => item.nameJP || item.name).map(item => ({
                                                                label: language === LANGUAGES_SUPPORTED.Japanese ? item.nameJP : item.name,
                                                                value: item.id.toString(),
                                                                code: item.id
                                                            }))
                                                        ] :
                                                        []
                                                    )
                                                }
                                            />
                                        </div>
                                    </FormControl>
                                </FormItem>
                            )}
                        />
                        <FormField
                            control={form.control}
                            name='provinceId'
                            render={({ field }) => (
                                <FormItem className='w-full'>
                                    <FormControl className=''>
                                        <div className='relative'>
                                            <MapPinHouseIcon className='absolute left-3 top-1/2 -translate-y-1/2 z-10 size-6 text-[#595959]' />
                                            <Combobox
                                                className='w-full pl-10'
                                                placeholder={t('field.prefectures')}
                                                value={field.value ?? ''}
                                                onChange={(item) => {
                                                    field.onChange(item?.code);
                                                    form.setValue('provinceId', item?.code || '');
                                                    setObservable('paging', defaultPaging, { isMergeObject: false });

                                                    onSearch({
                                                        provinceId: item?.code,
                                                        regionId: watchRegionId
                                                    });
                                                }}
                                                search={false}
                                                items={
                                                    (Array.isArray(provinces) && provinces?.length > 0 ?
                                                        [
                                                            ...provinces?.filter(item => item?.nameJP || item.name)?.map(item => ({
                                                                label: language === LANGUAGES_SUPPORTED.Japanese ? item.nameJP : item.name,
                                                                value: item?.id?.toString(),
                                                                code: item?.id
                                                            }))
                                                        ] :
                                                        []
                                                    )
                                                }
                                            />
                                        </div>
                                    </FormControl>
                                </FormItem>
                            )}
                        />
                        {/* <ButtonCus variant='black' size='md' className='w-full'>
                            {t('button.apply')}
                        </ButtonCus> */}
                    </div>
                </form>
            </Form>
            <div
                className='overflow-y-auto md:mt-6 mt-4'
                id='locations-register-modal'
            >
                {
                    nonFavoriteLocationsList?.length > 0 ?
                        (
                            <div
                                ref={locationListRef}
                                className='grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6 flex-1 pr-1'
                            >
                                {nonFavoriteLocationsList?.map(location => (
                                    <LocationCard key={location?.id} location={location} isFavorite={false} />
                                ))}
                            </div>
                        ) :
                        (
                            <div className='flex justify-center items-center w-full min-h-[390px]'>{t('sentences.no_data')}</div>
                        )
                }
            </div>
            {totalPage > 0 && (
                <div className='md:mt-6 mt-4 flex items-center justify-center'>
                    <Paginate
                        isPagenateEU
                        forcePage={paging.pagination.pageIndex}
                        pageCount={totalPage}
                        onPageChange={({ selected }) => {
                            paging.pagination.pageIndex = selected;
                            getListFavoriteLocationsByStatus(searchParams, paging);
                            onScrollToTopList();
                        }}
                        pageRangeDisplayed={2}
                        pageLinkClassName='px-1.5 xsm:px-2.5 min-w-fit xsm:min-w-[30px]'
                        activeLinkClassName='px-1.5 xsm:px-2.5'
                    />
                </div>
            )}
        </div>
    );
});
// #endregion
